import React, { useState } from 'react';
import PropTypes from 'prop-types';
import {
  Image, Box, Text, Flex,
} from 'rebass/styled-components';
import styled from 'styled-components';
import { FiLogOut } from 'react-icons/fi';
import { useHover } from 'react-use';
import posed, { Transition } from 'react-pose';
import { fontWeight } from 'styled-system';
import theme from 'styled-theming';
import { Toggle, Button } from '../components';

import PersonSVG from '../assets/person.svg';
import { useAppState } from '../hooks';

const backgroundColor = theme('mode', {
  light: (props) => props.theme.colors.white,
  dark: (props) => props.theme.colors.royalBlue,
});

const borderColor = theme('mode', {
  light: (props) => props.theme.colors.lightPurple,
  dark: (props) => props.theme.colors.greyBlue,
});

const textColor = theme('mode', {
  light: (props) => props.theme.colors.darkGrey,
  dark: (props) => props.theme.colors.midLightGrey,
});

const labelColor = theme('mode', {
  light: (props) => props.theme.colors.darkGrey,
  dark: (props) => props.theme.colors.lightBlue,
});

const Label = styled(Text)`
  color: ${labelColor};
`;

const AccountInfoWrapper = styled(Box)`
  position: absolute;
  bottom: 24px;
  left: 14px;
  width: 32px;
`;

const DropdownWrapper = styled(Flex)`
  position: absolute;
  bottom: -8px;
  left: 36px;
  z-index: 1;
`;

const DropdownContainer = styled(Box)`
  background-color: ${backgroundColor};
  border-radius: ${(props) => props.theme.radius.small};
  border: 1px solid ${borderColor};
`;

const ImageWrapper = styled(Box)`
  transition: ${(props) => props.theme.animation.quick};
  border: 1px solid ${borderColor};
  border-radius: ${(props) => props.theme.radius.biggest};
  box-shadow: ${(props) => props.theme.shadows.small};
  &:hover {
    box-shadow: ${(props) => props.theme.shadows.medium};
  }
`;

const BlockImage = styled(Image)`
  display: block;
`;

const AccountDetail = styled(Box)`
  ${fontWeight}
  white-space: nowrap;
  color: ${textColor};
`;

function DropdownMenu(props) {
  const {
    userId,
  } = props;
  const {
    setTheme, logout: handleLogout, accountId: brandId, themeMode,
  } = useAppState();

  const isDev = process.env.NODE_ENV === 'development';
  const isLightTheme = themeMode === 'light';

  return (
    <DropdownWrapper alignItems="flex-end">
      <DropdownContainer p={3}>
        {isDev && (
          <Box mb={2}>
            <Label fontSize={0} fontWeight={500} mb={2}>Theme (dev only)</Label>
            <Toggle
              className={isLightTheme ? null : 'dark-theme'}
              icons={false}
              checked={isLightTheme}
              onChange={() => setTheme(isLightTheme ? 'dark' : 'light')}
            />
          </Box>
        )}
        <Label fontSize={0} fontWeight={500} mb={2}>Account Info</Label>
        {brandId && (
          <AccountDetail fontSize={0} fontWeight={300} py={1}>
            {`Brand ID: ${brandId}`}
          </AccountDetail>
        )}
        {userId && (
          <AccountDetail fontSize={0} fontWeight={300} py={1}>
            {`User ID: ${userId}`}
          </AccountDetail>
        )}
        {isDev && (
          <AccountDetail fontSize={0} fontWeight={300} py={1}>
            {`Environment: ${process.env.NODE_ENV}`}
          </AccountDetail>
        )}
        {isDev && process.env.REACT_APP_BUILD_NUMBER && (
          <AccountDetail fontSize={0} fontWeight={300} py={1}>
            {`Build Number: ${process.env.REACT_APP_BUILD_NUMBER}`}
          </AccountDetail>
        )}
        {isDev && process.env.REACT_APP_GIT_COMMIT && (
          <AccountDetail fontSize={0} fontWeight={300} py={1}>
            {`Git Commit: ${process.env.REACT_APP_GIT_COMMIT}`}
          </AccountDetail>
        )}
        <Label fontSize={0} fontWeight={500} mt={3} mb={2} />
        <Button variant="secondary" onClick={handleLogout || undefined}>
          <Box mr={2}>Logout</Box>
          <FiLogOut />
        </Button>
      </DropdownContainer>
    </DropdownWrapper>
  );
}

DropdownMenu.propTypes = {
  userId: PropTypes.string.isRequired,
};

const AnimatedBox = posed(styled(Box)`
  &&& {
    position: relative;
  }
`)({
  enter: { opacity: 1, transition: { duration: 250 } },
  exit: { opacity: 0, transition: { duration: 250 } },
});

function AccountInfo(props) {
  const [menuIsOpen, setMenuIsOpen] = useState(true);

  const handleClick = () => setMenuIsOpen(!menuIsOpen);

  const {
    handleLogout, brandId, userId, themeMode,
  } = props;

  const HoverComponent = (hoverable) => (
    <AccountInfoWrapper>
      <ImageWrapper onClick={handleClick}>
        <BlockImage src={PersonSVG} alt="Account" height={30} width={30} p="1px" />
      </ImageWrapper>
      <Transition>
        {hoverable && (
          <AnimatedBox key="dropdown">
            <DropdownMenu
              handleLogout={handleLogout}
              userId={userId}
              brandId={brandId}
              themeMode={themeMode}
            />
          </AnimatedBox>
        )}
      </Transition>
    </AccountInfoWrapper>
  );

  const [hoverable] = useHover(HoverComponent);

  return hoverable;
}

AccountInfo.defaultProps = {
  brandId: '',
  userId: '',
  ref: undefined,
};

AccountInfo.propTypes = {
  brandId: PropTypes.string,
  userId: PropTypes.string,
  themeMode: PropTypes.string.isRequired,
  ref: PropTypes.object,
};

export default AccountInfo;
